import { Plus, X } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useNotify, useRefresh } from "ra-core";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider, LinkObjectType } from "../providers/types";
import { LINK_ERRORS, type LinkChoice, linkChoices } from "./linkChoices";
import { type PickedRecord, RecordPicker } from "./RecordPicker";

const SELECT_CLASS =
  "border-input h-9 w-full rounded-md border bg-transparent px-3 text-sm shadow-xs";

/**
 * Add link (W9 F5): pick how this record relates, find the other record,
 * save. The record's page reloads to show the new link at this end; the
 * other record shows it too.
 */
export function GraphLinkEditor({
  objectType,
  objectId,
}: {
  objectType: LinkObjectType;
  objectId: string;
}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefresh();
  const notify = useNotify();
  const [isOpen, setIsOpen] = useState(false);
  const [choiceKey, setChoiceKey] = useState("");
  const [picked, setPicked] = useState<PickedRecord | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const types = useQuery({
    queryKey: ["link_types"],
    queryFn: () => dataProvider.getLinkTypes(),
    enabled: isOpen,
  });
  const choices = linkChoices(types.data ?? [], objectType);
  const choice: LinkChoice | undefined =
    choices.find((c) => c.key === choiceKey) ?? choices[0];

  const reset = () => {
    setIsOpen(false);
    setPicked(null);
  };

  const save = async () => {
    if (!choice || !picked) return;
    const here = { type: objectType, id: objectId };
    const there = { type: choice.otherType, id: picked.id };
    const [from, to] = choice.isFrom ? [here, there] : [there, here];
    setIsSaving(true);
    try {
      await dataProvider.createLink({
        link_type_id: choice.linkTypeId,
        from_type: from.type,
        from_id: from.id,
        to_type: to.type,
        to_id: to.id,
      });
      reset();
      refresh();
    } catch (error) {
      const code = (error as { body?: { error?: string } }).body?.error;
      notify(LINK_ERRORS[code ?? ""] ?? "The link could not be saved", {
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return (
      <Button
        className="mt-3 -ml-2 text-muted-foreground"
        size="sm"
        variant="ghost"
        onClick={() => setIsOpen(true)}
      >
        <Plus className="size-4" />
        Add link
      </Button>
    );
  }
  return (
    <form
      aria-label="Add link"
      className="mt-3 max-w-md space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="link-type">Link</Label>
        <select
          id="link-type"
          className={SELECT_CLASS}
          value={choice?.key ?? ""}
          onChange={(event) => {
            setChoiceKey(event.target.value);
            setPicked(null);
          }}
        >
          {choices.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
      </div>
      {choice ? (
        <RecordPicker
          key={choice.otherType}
          id="link-search"
          label="Find the other record"
          objectType={choice.otherType}
          excludeId={objectId}
          picked={picked}
          onPick={setPicked}
          pickedText="Linking to"
        />
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!picked || isSaving}>
          Save link
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={reset}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** End (W9 F5): a link is ended, not deleted, and leaves both pages. */
export function EndLinkButton({
  linkId,
  label,
}: {
  linkId: string;
  label: string;
}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefresh();
  const notify = useNotify();
  const [isEnding, setIsEnding] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-7 text-muted-foreground hover:text-foreground"
      aria-label={`End link to ${label}`}
      disabled={isEnding}
      onClick={async () => {
        setIsEnding(true);
        try {
          await dataProvider.endLink(linkId);
          refresh();
        } catch {
          notify("The link could not be ended", { type: "error" });
        } finally {
          setIsEnding(false);
        }
      }}
    >
      <X className="size-4" />
    </Button>
  );
}
